import { GetStaticProps, InferGetStaticPropsType, GetStaticPaths } from 'next';

import { CaseStudyListPage } from '@components';
import { casestudyListPath, hnaviCategoryPath } from '@paths';
import { useCanonical } from '@hooks';
import {
  hnaviTitle,
  hnaviShortTitle,
  wpCustomFieldNames,
  casestudyTypes,
  isrRevalidateSec,
} from '@constants';
import {
  getAllCategoryNamesAndSlugs2,
  getCategory,
  getHnaviOptions,
  getLatestPosts,
  getMostPopularPosts,
  getPosts,
} from '@api';
import {
  getPageOffset,
  PER_PAGE_REST,
  calcPageCountFromPostCount,
  getHnaviCategoryMetaQuery,
} from '@utils';
import { getHnaviContentCategoriesFromCategories } from '@helpers';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  categoryInfo,
  categorySlug,
  hnaviCategoryCode,
  hnaviCategoryName,
  casestudyType,
  ...rest
}) => {
  const canonical = useCanonical();
  return (
    <CaseStudyListPage
      {...rest}
      pageTitle={`${hnaviCategoryName ? hnaviCategoryName + 'の' : ''}${
        rest.casestudyTypeName
      }`}
      seoData={{
        ...categoryInfo?.seo,
        title: `${rest.casestudyTypeName} | ${hnaviTitle}`,
        canonical,
      }}
      hnaviCategory={hnaviCategoryCode}
      onHnaviCategorySelectChange={({ currentTarget }) => {
        location.href = casestudyListPath(casestudyType, currentTarget.value);
      }}
      paginationBasePath={casestudyListPath(casestudyType, hnaviCategoryCode)}
      breadcrumbs={[
        {
          text: hnaviTitle,
          shortText: hnaviShortTitle,
          url: '/',
        },
        ...(hnaviCategoryCode
          ? [
              {
                text: hnaviCategoryName,
                url: hnaviCategoryPath(hnaviCategoryCode),
              },
            ]
          : []),
        { text: rest.casestudyTypeName },
      ]}
    />
  );
};

export default Page;

type CasestudyType = keyof typeof casestudyTypes;

export const getStaticProps = async ({
  params,
}: Parameters<
  GetStaticProps<
    any,
    { casestudyType: CasestudyType; pageNo?: string; hnaviCategory?: string }
  >
>[0]) => {
  const categorySlug = 'interview';

  const casestudyTypeName = params?.casestudyType
    ? casestudyTypes[params.casestudyType] || null
    : null;

  if (!casestudyTypeName) {
    return { notFound: true, revalidate: isrRevalidateSec } as never;
  }

  const { totalPostCount, posts } = params?.casestudyType
    ? await getPosts({
        where: {
          categoryName: categorySlug,
          offsetPagination: {
            size: PER_PAGE_REST,
            offset: getPageOffset(Number(params?.pageNo)),
          },
          metaQuery: {
            metaArray: [
              {
                key: wpCustomFieldNames.hnaviReferenceKey,
                value: params.casestudyType,
              },
              ...(params.hnaviCategory
                ? [getHnaviCategoryMetaQuery(params.hnaviCategory)]
                : []),
            ],
          },
        },
      })
    : { totalPostCount: null, posts: null };

  console.info(`getStaticProps /${params?.casestudyType}/${params?.pageNo}`);

  const categories = await getAllCategoryNamesAndSlugs2();
  const hnaviContentCategories =
    getHnaviContentCategoriesFromCategories(categories);

  return {
    props: {
      posts,
      casestudyType: params?.casestudyType || null,
      casestudyTypeName,
      hnaviCategoryCode: params?.hnaviCategory || null,
      hnaviCategoryName:
        (hnaviContentCategories as any)[params?.hnaviCategory || ''] || null,
      categorySlug,
      categoryInfo: (await getCategory(categorySlug || '')) || null,
      pageNo: Number(params?.pageNo) || 1,
      pageCount: calcPageCountFromPostCount(totalPostCount || 1),
      categories,
      hnaviContentCategories,
      latestPosts: await getLatestPosts(),
      popularPosts: await getMostPopularPosts(),
      relatedTokushuPosts:
        (
          await getPosts({
            where: {
              metaQuery: {
                metaArray: [
                  {
                    key: wpCustomFieldNames.hnaviReferenceKey,
                    value: 'feature',
                  },
                  ...(params?.hnaviCategory
                    ? [getHnaviCategoryMetaQuery(params.hnaviCategory)]
                    : []),
                ],
              },
            },
            first: 5,
          })
        ).posts || null,
      hnaviOptions: await getHnaviOptions(),
    },
    revalidate: isrRevalidateSec,
  };
};

export const getStaticPaths: GetStaticPaths<{
  casestudyType: CasestudyType;
}> = async () => {
  return {
    paths: (Object.keys(casestudyTypes) as (keyof typeof casestudyTypes)[]).map(
      (casestudyType) => ({
        params: { casestudyType },
      })
    ),
    fallback: false,
  };
};
